import axios from 'axios';

// Add API URL logging on application startup
const apiBaseUrl = '/api';
console.log('[Axios Config] Using API base URL:', apiBaseUrl);
console.log('[Axios Config] Full API URL from browser:', `${window.location.origin}${apiBaseUrl}`);

// Get the full origin-based URL (helpful for direct API calls)
const getFullApiUrl = () => {
  return `${window.location.origin}${apiBaseUrl}`;
};

const axiosInstance = axios.create({
  baseURL: apiBaseUrl,
  timeout: 120000, // 2 minute timeout
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
  // Add retry configuration
  retry: 3, // Number of retry attempts
  retryDelay: 1000 // Initial retry delay in ms (will increase with backoff)
});

// Enable request debugging to console
const DEBUG_AXIOS = true;

// Endpoints to exclude from verbose logging (to reduce console noise)
const QUIET_ENDPOINTS = [
  '/notifications/recent', // Exclude notification polling
  '/health'                // Exclude health checks
];

// Crucial endpoints that should get special retry handling
const CRITICAL_ENDPOINTS = [
  '/calendar/events',
  '/jobs',
  '/customers'
];

// Helper function for friendly error messages
const getFriendlyErrorMessage = (error) => {
  if (error.code === 'ECONNABORTED') {
    return 'The request timed out. Please try again.';
  }
  if (!error.response) {
    return 'Unable to connect to the server. Please check your internet connection.';
  }
  switch (error.response.status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'You need to login to access this feature.';
    case 403:
      return 'You don\'t have permission to access this resource.';
    case 404:
      return 'The requested resource was not found.';
    case 500:
      return 'An internal server error occurred. Please try again later.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

// Helper to check if an endpoint should be quiet logged
const isQuietEndpoint = (url) => {
  return QUIET_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

// Helper to check if an endpoint is critical (needs special retry logic)
const isCriticalEndpoint = (url) => {
  return CRITICAL_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

// Function to determine if a request should be retried
const shouldRetry = (error) => {
  // Only retry if it's a network error, 408 (timeout) or 5xx error
  const isNetworkError = !error.response;
  const isTimeout = error.code === 'ECONNABORTED';
  const is5xxError = error.response && error.response.status >= 500 && error.response.status < 600;
  
  // Get the current retry attempt
  const config = error.config;
  const currentRetryAttempt = config.__retryCount || 0;
  
  // Get the max retries from config
  const maxRetries = config.retry || 3;
  
  // Check if it's a critical endpoint (to give special treatment)
  const isCritical = config.url && isCriticalEndpoint(config.url);
  
  // Allow more retries for critical endpoints
  const effectiveMaxRetries = isCritical ? maxRetries + 2 : maxRetries;
  
  // Debug log
  if (DEBUG_AXIOS && !isQuietEndpoint(config.url)) {
    console.log(`[Axios Retry] Evaluating retry for ${config.url}:`, {
      isNetworkError,
      isTimeout,
      is5xxError,
      currentAttempt: currentRetryAttempt,
      maxRetries: effectiveMaxRetries,
      isCritical
    });
  }
  
  return (isNetworkError || isTimeout || is5xxError) && currentRetryAttempt < effectiveMaxRetries;
};

// Request interceptor
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  const timestamp = new Date().toISOString();
  
  // Check if this is a "quiet" endpoint (like polling)
  const isQuiet = isQuietEndpoint(config.url);
  
  if (DEBUG_AXIOS && !isQuiet) {
    console.log(`[Axios Debug] Request interceptor:`, {
      timestamp,
      hasToken: !!token,
      url: config.url,
      method: config.method
    });
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    if (DEBUG_AXIOS && !isQuiet) console.log('[Axios Debug] Token added to request headers');
  } else {
    if (DEBUG_AXIOS && !isQuiet) console.log('[Axios Debug] No token found in localStorage');
  }
  
  // Ensure URL starts with a slash
  if (!config.url.startsWith('/')) {
    config.url = `/${config.url}`;
  }
  
  // Remove any double slashes in the URL
  config.url = config.url.replace(/\/+/g, '/');

  if (DEBUG_AXIOS && !isQuiet) {
    console.log(`[${timestamp}] Request:`, {
      method: config.method,
      url: config.url,
      data: config.data,
      headers: config.headers
    });

    console.log(`[${timestamp}] Final URL:`, `${config.baseURL}${config.url}`);
  }
  
  return config;
}, (error) => {
  console.error('[Axios Debug] Request interceptor error:', error);
  return Promise.reject(error);
});

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    console.log(`[DEBUG] API Response:`, {
      status: response.status,
      url: response.config.url,
      method: response.config.method,
      data: response.data ? (typeof response.data === 'object' ? 'object' : response.data) : null
    });
    return response;
  },
  async (error) => {
    // Enhanced network error logging
    if (error.message && error.message.includes('Network Error')) {
      console.error('[DEBUG-CRITICAL] Network Error Details:', {
        message: error.message,
        code: error.code || 'N/A',
        stack: error.stack,
        config: {
          url: error.config?.url || 'N/A',
          method: error.config?.method || 'N/A',
          baseURL: error.config?.baseURL || 'N/A',
          timeout: error.config?.timeout || 'N/A',
          headers: error.config?.headers || 'N/A'
        }
      });
      
      // Try to detect if the server is reachable
      try {
        console.log('[DEBUG-CRITICAL] Attempting API health check');
        const healthCheckUrl = getFullApiUrl() + '/health';
        const healthResponse = await fetch(healthCheckUrl, { 
          method: 'GET',
          cache: 'no-cache'
        });
        console.log('[DEBUG-CRITICAL] Health check result:', {
          status: healthResponse.status,
          ok: healthResponse.ok,
          text: await healthResponse.text()
        });
      } catch (healthError) {
        console.error('[DEBUG-CRITICAL] Health check failed:', healthError.message);
      }
    }

    console.log(`[DEBUG] API Error:`, {
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      message: error.message,
      response: error.response?.data
    });

    const timestamp = new Date().toISOString();
    const config = error.config || {};
    const isQuiet = config.url && isQuietEndpoint(config.url);
    
    if (DEBUG_AXIOS && !isQuiet) {
      console.log('[Axios Debug] Response error:', {
        timestamp,
        url: config.url,
        method: config.method,
        status: error.response?.status,
        message: error.message
      });
    }

    // Handle retry logic (for network errors, timeouts, etc.)
    if (shouldRetry(error)) {
      config.__retryCount = (config.__retryCount || 0) + 1;
      
      if (DEBUG_AXIOS && !isQuiet) {
        console.log(`[Axios Retry] Retrying request to ${config.url} (Attempt ${config.__retryCount})`);
      }
      
      // Use exponential backoff for retries
      const delay = config.retryDelay || 1000;
      const exponentialDelay = delay * Math.pow(2, config.__retryCount - 1);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, exponentialDelay));
      
      // Special handling for network errors and critical endpoints
      if (!error.response && isCriticalEndpoint(config.url)) {
        if (DEBUG_AXIOS && !isQuiet) {
          console.log(`[Axios Retry] Critical endpoint with network error, trying with full URL`);
        }
        
        // Try with full origin URL if it's a network error on a critical endpoint
        const fullUrl = getFullApiUrl() + config.url;
        config.baseURL = ''; // Remove base URL
        config.url = fullUrl; // Use full URL directly
      }
      
      // Make a new request
      return axiosInstance(config);
    }

    // Token refresh handling
    if (error.response?.status === 401 && !config._retry) {
      if (DEBUG_AXIOS && !isQuiet) console.log('[Axios Debug] Attempting token refresh');
      config._retry = true;
      try {
        const response = await axios.post('/api/auth/refresh-token', {}, { 
          withCredentials: true 
        });
        
        if (response.data.token) {
          if (DEBUG_AXIOS && !isQuiet) console.log('[Axios Debug] Token refresh successful');
          localStorage.setItem('token', response.data.token);
          axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          return axiosInstance(config);
        } else {
          if (DEBUG_AXIOS && !isQuiet) console.log('[Axios Debug] Token refresh response missing token');
        }
      } catch (refreshError) {
        if (DEBUG_AXIOS && !isQuiet) {
          console.error('[Axios Debug] Token refresh failed:', {
            status: refreshError.response?.status,
            message: refreshError.message
          });
        }
        
        if (refreshError.response?.status === 401) {
          if (DEBUG_AXIOS && !isQuiet) console.log('[Axios Debug] Clearing token and redirecting to signin');
          localStorage.removeItem('token');
          window.location.href = '/signin';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Export both the instance and helper functions
export { getFullApiUrl };
export default axiosInstance;

